import React, { useRef, useState } from 'react';
import {
  ArrowUpTrayIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { Button, Modal } from '../ui';
import { clientsAPI } from '../../services/api';
import { useNotifications } from '../../contexts/NotificationContext';
import toast from 'react-hot-toast';

type DuplicateMode = 'skip' | 'update';
type WizardStep = 'upload' | 'preview' | 'result';

type PreviewRow = {
  row: number;
  action: 'create' | 'update' | 'skip';
  clientId?: number;
  carnetNo: string;
  name: string;
  lastname: string;
  cedula: string;
  dateOfBirth: string;
  address: string;
  gremio: string;
  planTemplateId: string;
  cuotaMensual: string;
  telefonos: string;
};

type PreviewData = {
  total: number;
  valid: number;
  toCreate: number;
  toUpdate: number;
  toSkip: number;
  errors: Array<{ row: number; field: string; message: string }>;
  duplicateMode: DuplicateMode;
  rows?: PreviewRow[];
};

type PreviewFilter = 'all' | 'create' | 'update' | 'skip';

const ACTION_LABELS: Record<PreviewRow['action'], string> = {
  create: 'Crear',
  update: 'Actualizar',
  skip: 'Omitir'
};

const ACTION_BADGE: Record<PreviewRow['action'], string> = {
  create: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  update: 'bg-sky-100 text-sky-800 ring-sky-200',
  skip: 'bg-slate-100 text-slate-600 ring-slate-200'
};

function fullName(row: PreviewRow) {
  return [row.name, row.lastname].filter(Boolean).join(' ').trim() || '—';
}

type ImportResult = {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: Array<{ row: number; field: string; message: string }>;
  duplicateMode: DuplicateMode;
};

type ClientImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
};

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function formatImportSummary(data: ImportResult) {
  return `${data.created} creados, ${data.updated} actualizados, ${data.skipped} omitidos, ${data.failed} fallidos`;
}

export const ClientImportModal: React.FC<ClientImportModalProps> = ({
  isOpen,
  onClose,
  onImported
}) => {
  const { fetchNotifications } = useNotifications();
  const [step, setStep] = useState<WizardStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [duplicateMode, setDuplicateMode] = useState<DuplicateMode>('skip');
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [previewFilter, setPreviewFilter] = useState<PreviewFilter>('all');
  const inputRef = useRef<HTMLInputElement>(null);

  const resetState = () => {
    setStep('upload');
    setFile(null);
    setDuplicateMode('skip');
    setPreview(null);
    setResult(null);
    setLoading(false);
    setIsDragging(false);
    setPreviewFilter('all');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const acceptFile = (next: File | null) => {
    if (!next) return;
    const name = next.name.toLowerCase();
    if (!name.endsWith('.csv') && !name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      toast.error('Solo se permiten archivos CSV o Excel');
      return;
    }
    if (next.size > 5 * 1024 * 1024) {
      toast.error('El archivo supera 5 MB');
      return;
    }
    setFile(next);
    setPreview(null);
    setResult(null);
    setStep('upload');
  };

  const downloadTemplate = async (format: 'csv' | 'xlsx') => {
    try {
      const response = await clientsAPI.downloadTemplate(format);
      const mime =
        format === 'csv'
          ? 'text/csv;charset=utf-8'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mime });
      triggerBlobDownload(blob, `plantilla-clientes.${format}`);
      toast.success(`Plantilla ${format.toUpperCase()} descargada`);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'No se pudo descargar la plantilla');
    }
  };

  const runPreview = async () => {
    if (!file) {
      toast.error('Seleccione un archivo');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('duplicateMode', duplicateMode);
      const response = await clientsAPI.importPreview(formData);
      setPreview(response.data.data);
      setStep('preview');
      const data = response.data.data;
      toast.success(
        `Análisis listo: ${data.toCreate} a crear, ${duplicateMode === 'update' ? data.toUpdate : data.toSkip} a ${duplicateMode === 'update' ? 'actualizar' : 'omitir'}`
      );
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Error al analizar el archivo');
    } finally {
      setLoading(false);
    }
  };

  const runImport = async () => {
    if (!file) return;
    setLoading(true);
    const loadingToast = toast.loading('Importando clientes…');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('duplicateMode', duplicateMode);
      const response = await clientsAPI.import(formData);
      const data = response.data.data as ImportResult;
      setResult(data);
      setStep('result');
      onImported();
      await fetchNotifications();
      if (data.failed > 0) {
        toast.error(`Importación con errores: ${formatImportSummary(data)}`, { id: loadingToast });
      } else {
        toast.success(`Importación completada: ${formatImportSummary(data)}`, { id: loadingToast });
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Error al importar', { id: loadingToast });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Importar clientes"
      size={step === 'preview' ? 'xl' : 'lg'}
    >
      {step === 'upload' && (
        <div className="space-y-5">
          <p className="text-sm text-slate-600">
            Suba un CSV o Excel con los datos de clientes. Puede incluir{' '}
            <code className="rounded bg-slate-100 px-1 text-xs">planTemplateId</code> y cuota por fila.
          </p>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => downloadTemplate('xlsx')}
              className="inline-flex items-center"
            >
              <DocumentArrowDownIcon className="mr-1.5 h-4 w-4" />
              Plantilla Excel
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => downloadTemplate('csv')}
              className="inline-flex items-center"
            >
              <DocumentArrowDownIcon className="mr-1.5 h-4 w-4" />
              Plantilla CSV
            </Button>
          </div>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              const dropped = e.dataTransfer.files?.[0];
              if (dropped) acceptFile(dropped);
            }}
            className={`rounded-xl border-2 border-dashed p-8 text-center transition ${
              isDragging
                ? 'border-primary-400 bg-primary-50'
                : 'border-slate-200 bg-slate-50/60'
            }`}
          >
            <ArrowUpTrayIcon className="mx-auto h-10 w-10 text-slate-400" />
            <p className="mt-3 text-sm font-medium text-slate-800">
              Arrastre el archivo aquí o elija uno
            </p>
            <p className="mt-1 text-xs text-slate-500">CSV o Excel · máximo 5 MB · hasta 2000 filas</p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="mt-4"
              onClick={() => inputRef.current?.click()}
            >
              Seleccionar archivo
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => acceptFile(e.target.files?.[0] || null)}
            />
            {file ? (
              <div className="mt-4 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                <DocumentTextIcon className="h-4 w-4 text-slate-400" />
                {file.name}
              </div>
            ) : null}
          </div>

          <fieldset>
            <legend className="text-sm font-semibold text-slate-800">
              Si el cliente ya existe (misma cédula o carnet)
            </legend>
            <div className="mt-2 space-y-2">
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-3 hover:bg-slate-50">
                <input
                  type="radio"
                  name="duplicateMode"
                  className="mt-1"
                  checked={duplicateMode === 'skip'}
                  onChange={() => setDuplicateMode('skip')}
                />
                <span>
                  <span className="block text-sm font-medium text-slate-900">Omitir</span>
                  <span className="text-xs text-slate-500">
                    No modificar registros existentes; solo crear nuevos.
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-3 hover:bg-slate-50">
                <input
                  type="radio"
                  name="duplicateMode"
                  className="mt-1"
                  checked={duplicateMode === 'update'}
                  onChange={() => setDuplicateMode('update')}
                />
                <span>
                  <span className="block text-sm font-medium text-slate-900">Actualizar</span>
                  <span className="text-xs text-slate-500">
                    Sobrescribir datos (y plan/cuota si vienen en la fila).
                  </span>
                </span>
              </label>
            </div>
          </fieldset>

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="button" onClick={runPreview} disabled={!file || loading}>
              {loading ? 'Analizando…' : 'Continuar'}
            </Button>
          </div>
        </div>
      )}

      {step === 'preview' && preview && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{preview.total}</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Crear</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-emerald-800">{preview.toCreate}</p>
            </div>
            <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">
                {duplicateMode === 'update' ? 'Actualizar' : 'Omitir'}
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-sky-800">
                {duplicateMode === 'update' ? preview.toUpdate : preview.toSkip}
              </p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Errores</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-amber-800">
                {preview.errors.length}
              </p>
            </div>
          </div>

          {preview.rows && preview.rows.length > 0 ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-800">Vista previa de datos</p>
                <div className="flex flex-wrap gap-1.5">
                  {(['all', 'create', 'update', 'skip'] as PreviewFilter[]).map((filter) => {
                    const label =
                      filter === 'all'
                        ? 'Todos'
                        : filter === 'create'
                          ? 'Crear'
                          : filter === 'update'
                            ? 'Actualizar'
                            : 'Omitir';
                    const count =
                      filter === 'all'
                        ? preview.rows!.length
                        : preview.rows!.filter((r) => r.action === filter).length;
                    return (
                      <button
                        key={filter}
                        type="button"
                        onClick={() => setPreviewFilter(filter)}
                        className={`rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
                          previewFilter === filter
                            ? 'bg-slate-900 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {label} ({count})
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="max-h-64 overflow-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Fila</th>
                      <th className="px-3 py-2">Acción</th>
                      <th className="px-3 py-2">Nombre</th>
                      <th className="px-3 py-2">Cédula</th>
                      <th className="px-3 py-2">Carnet</th>
                      <th className="px-3 py-2">Dirección</th>
                      <th className="px-3 py-2">Plan</th>
                      <th className="px-3 py-2">Cuota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows
                      .filter((row) => previewFilter === 'all' || row.action === previewFilter)
                      .map((row) => (
                        <tr key={`${row.row}-${row.action}`} className="border-t border-slate-100">
                          <td className="px-3 py-2 tabular-nums text-slate-600">{row.row}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${ACTION_BADGE[row.action]}`}
                            >
                              {ACTION_LABELS[row.action]}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-medium text-slate-900">{fullName(row)}</td>
                          <td className="px-3 py-2 tabular-nums text-slate-700">{row.cedula || '—'}</td>
                          <td className="px-3 py-2 text-slate-600">{row.carnetNo || '—'}</td>
                          <td className="max-w-[180px] truncate px-3 py-2 text-slate-600" title={row.address}>
                            {row.address || '—'}
                          </td>
                          <td className="px-3 py-2 tabular-nums text-slate-600">
                            {row.planTemplateId || '—'}
                          </td>
                          <td className="px-3 py-2 tabular-nums text-slate-600">
                            {row.cuotaMensual || '—'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {preview.errors.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-amber-800">Errores de validación</p>
              <div className="max-h-40 overflow-auto rounded-xl border border-amber-200 bg-amber-50/30">
                <table className="min-w-full text-left text-sm">
                  <thead className="sticky top-0 bg-amber-50 text-xs uppercase text-amber-800">
                    <tr>
                      <th className="px-3 py-2">Fila</th>
                      <th className="px-3 py-2">Campo</th>
                      <th className="px-3 py-2">Mensaje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.errors.slice(0, 100).map((err, idx) => (
                      <tr key={`${err.row}-${err.field}-${idx}`} className="border-t border-amber-100">
                        <td className="px-3 py-2 tabular-nums text-slate-700">{err.row}</td>
                        <td className="px-3 py-2 text-slate-600">{err.field}</td>
                        <td className="px-3 py-2 text-slate-800">{err.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : preview.rows && preview.rows.length > 0 ? (
            <p className="text-sm text-slate-600">Sin errores de validación. Listo para importar.</p>
          ) : null}

          <div className="flex justify-between gap-2 border-t border-slate-100 pt-4">
            <Button type="button" variant="secondary" onClick={() => setStep('upload')} disabled={loading}>
              Atrás
            </Button>
            <Button
              type="button"
              onClick={runImport}
              disabled={loading || (preview.toCreate === 0 && preview.toUpdate === 0)}
            >
              {loading ? 'Importando…' : 'Confirmar importación'}
            </Button>
          </div>
        </div>
      )}

      {step === 'result' && result && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Creados</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-emerald-800">{result.created}</p>
            </div>
            <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">Actualizados</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-sky-800">{result.updated}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Omitidos</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{result.skipped}</p>
            </div>
            <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-700">Fallidos</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-rose-800">{result.failed}</p>
            </div>
          </div>

          {result.errors.length > 0 ? (
            <div className="max-h-48 overflow-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Fila</th>
                    <th className="px-3 py-2">Campo</th>
                    <th className="px-3 py-2">Mensaje</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.slice(0, 100).map((err, idx) => (
                    <tr key={`${err.row}-${err.field}-${idx}`} className="border-t border-slate-100">
                      <td className="px-3 py-2 tabular-nums text-slate-700">{err.row}</td>
                      <td className="px-3 py-2 text-slate-600">{err.field}</td>
                      <td className="px-3 py-2 text-slate-800">{err.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="flex justify-end border-t border-slate-100 pt-4">
            <Button type="button" onClick={handleClose}>
              Cerrar
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ClientImportModal;
